import React from "react";
import { useLang } from "@/i18n/LanguageContext";
import SEO from "@/components/SEO";
import { Heart, Star, ShieldCheck, Sparkles, Ticket, FileCheck, Building2, Users, Utensils, Package as PackageIcon, Bus, Landmark, Award, HandHeart, Clock, Gift } from "lucide-react";
import ReferralLeaderboard from "@/components/ReferralLeaderboard";

const API = process.env.REACT_APP_BACKEND_URL;
const HERO = `${API}/api/static/hero/about.jpg`;
const STORY1 = `${API}/api/static/hero/gallery.jpg`;
const STORY2 = `${API}/api/static/hero/packages.jpg`;

export default function About() {
  const { t } = useLang();
  const values = [
    { i: ShieldCheck, t: t("about.v1"), d: t("about.v1d") },
    { i: Heart, t: t("about.v2"), d: t("about.v2d") },
    { i: Star, t: t("about.v3"), d: t("about.v3d") },
    { i: Sparkles, t: t("about.v4"), d: t("about.v4d") },
  ];

  return (
    <div data-testid="about-page">
      <SEO
        title="Tentang Kami — Travel Umrah & Haji Resmi PPIU & PIHK"
        titleEn="About Us — Official PPIU & PIHK Umrah & Hajj Travel"
        description="PT Baitullah Tiga Kharisma (BATIK Tour & Travel) — biro perjalanan Umroh & Haji resmi. PPIU No. 91205097027470003 · PIHK No. 91205097027470005 · Akreditasi A Kemenag."
        descriptionEn="PT Baitullah Tiga Kharisma (BATIK Tour & Travel) — official Umrah & Hajj travel bureau. PPIU No. 91205097027470003, PIHK No. 91205097027470005, Accredited A by Indonesia's Religious Ministry."
      />
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28">
        <div className="container-x grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 anim-fade-up">
            <p className="overline mb-5">{t("sections.aboutOver")}</p>
            <h1 className="h-display text-primary text-balance">
              {t("about.hero")}
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-xl">{t("about.heroSub")}</p>
          </div>
          <div className="lg:col-span-5 anim-fade">
            <div className="aspect-[4/5] rounded-lg overflow-hidden border border-border">
              <img src={HERO} alt="Masjid interior" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 lg:py-32 bg-muted/40">
        <div className="container-x grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5 space-y-4">
            <div className="aspect-[4/3] rounded-lg overflow-hidden border border-border">
              <img src={STORY1} alt="Pilgrims" className="w-full h-full object-cover" />
            </div>
            <div className="aspect-[4/3] rounded-lg overflow-hidden border border-border ml-10">
              <img src={STORY2} alt="Group practice" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7 lg:pl-6">
            <p className="overline mb-5">{t("about.storyTitle")}</p>
            <h2 className="font-serif text-4xl lg:text-5xl text-primary tracking-tight">{t("about.storyTitle")}</h2>
            <div className="mt-8 space-y-6 text-muted-foreground leading-relaxed text-base">
              <p>{t("about.story1")}</p>
              <p>{t("about.story2")}</p>
              <p>{t("about.story3")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-24 lg:py-32" data-testid="mission-section">
        <div className="container-x grid lg:grid-cols-2 gap-10">
          <div className="bg-primary text-primary-foreground rounded-lg p-10 lg:p-14 relative overflow-hidden">
            <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "url('https://static.prod-images.emergentagent.com/jobs/c612f9b0-bd89-4c85-9451-5b81c8951306/images/165161635b2ebd7638221dd2398e9e8e9b9cc0c1d356138bcff47de1dc799d9f.png')", backgroundSize: "200px" }} />
            <p className="overline mb-5 !text-accent relative">{t("about.visionTitle")}</p>
            <h3 className="font-serif text-3xl lg:text-4xl relative">{t("about.visionTitle")}</h3>
            <p className="mt-6 text-white/80 leading-relaxed relative">{t("about.visionText")}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-10 lg:p-14">
            <p className="overline mb-5">{t("about.missionT")}</p>
            <h3 className="font-serif text-3xl lg:text-4xl text-primary">{t("about.missionT")}</h3>
            <ul className="mt-6 space-y-4">
              {[t("about.mission1"), t("about.mission2"), t("about.mission3"), t("about.mission4")].map((m, i) => (
                <li key={i} className="flex gap-4">
                  <span className="font-serif text-accent text-2xl leading-none">0{i + 1}</span>
                  <span className="text-muted-foreground pt-1.5">{m}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 lg:py-32 bg-card border-y border-border" data-testid="values-section">
        <div className="container-x">
          <div className="max-w-2xl mb-14">
            <p className="overline mb-5">{t("about.valuesTitle")}</p>
            <h2 className="font-serif text-4xl lg:text-5xl text-primary tracking-tight">{t("about.valuesTitle")}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <div key={i} className="p-8 border border-border rounded-lg bg-background hover:border-accent transition-colors" data-testid={`value-card-${i}`}>
                <v.i className="h-7 w-7 text-accent" strokeWidth={1.5} />
                <h3 className="font-serif text-2xl text-primary mt-5">{v.t}</h3>
                <p className="text-muted-foreground text-sm mt-3 leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Legalitas & Sejarah (from ptbatik.co.id/sejarah) */}
      <section className="py-24 lg:py-32 bg-primary text-white relative overflow-hidden" data-testid="sejarah-section">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
             style={{ backgroundImage: "url('https://static.prod-images.emergentagent.com/jobs/c612f9b0-bd89-4c85-9451-5b81c8951306/images/165161635b2ebd7638221dd2398e9e8e9b9cc0c1d356138bcff47de1dc799d9f.png')", backgroundSize: "220px" }} />
        <div className="container-x relative">
          <div className="max-w-3xl">
            <p className="overline !text-accent mb-5">Sejarah &amp; Legalitas</p>
            <h2 className="font-serif text-4xl lg:text-5xl text-white tracking-tight">Sejarah PT. Baitullah Tiga Kharisma</h2>
            <div className="mt-8 space-y-5 text-white/80 leading-relaxed text-base">
              <p>
                <strong className="text-white">PT. Baitullah Tiga Kharisma</strong> atau yang biasa disingkat dengan <strong className="text-accent">BATIK Travel</strong> adalah biro jasa Umrah dan Haji Khusus resmi yang memiliki izin
                <span className="text-accent"> PPIU No. U.565 Tahun 2020</span> dan <span className="text-accent">PIHK No. 91205097027470005</span>.
              </p>
              <p>
                Perusahaan yang telah diresmikan oleh Menkumham dengan nomor <span className="text-accent">AHU-0043738.AH.01.01 tahun 2016</span> ini,
                berupaya semaksimal mungkin untuk memberikan kebermanfaatan kepada kaum muslimin. Oleh karenanya, Batik Travel juga fokus mengadakan
                kajian-kajian Islam dan melebarkan sayap dalam bisnis impor komoditi dari Arab Saudi.
              </p>
              <p>
                Jangan khawatir untuk umrah ataupun bermuamalah bersama kami. Sebagian besar profit dialokasikan untuk mencerdaskan umat Islam
                di Indonesia — membantu pesantren, masjid, dan lembaga dakwah yang bisa kami jangkau.
              </p>
              <p className="text-accent italic">"Umrah Bersama Batik Travel — Insya Allah Berkesan dan Bermakna."</p>
            </div>
          </div>

          {/* Legalitas cards */}
          <div className="mt-14 grid md:grid-cols-3 gap-4">
            {[
              { label: "PPIU", value: "U.565 / 2020", desc: "Izin Penyelenggara Perjalanan Ibadah Umrah" },
              { label: "PIHK", value: "91205097027470005", desc: "Izin Penyelenggara Ibadah Haji Khusus" },
              { label: "Menkumham", value: "AHU-0043738.AH.01.01/2016", desc: "Pengesahan Badan Hukum Perseroan" },
            ].map((l, i) => (
              <div key={i} data-testid={`legalitas-${i}`} className="border border-white/15 bg-white/[0.03] rounded-lg p-6 backdrop-blur-sm">
                <p className="text-[10px] uppercase tracking-[0.25em] text-accent">{l.label}</p>
                <p className="mt-2 font-serif text-lg text-white break-all">{l.value}</p>
                <p className="mt-3 text-xs text-white/60">{l.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Moto Kami */}
      <ReferralLeaderboard />

      <section className="py-20 lg:py-28 bg-accent/5 border-y border-border" data-testid="moto-section">
        <div className="container-x">
          <div className="max-w-2xl mb-12">
            <p className="overline mb-5">Prinsip Kami</p>
            <h2 className="font-serif text-4xl lg:text-5xl text-primary tracking-tight">Moto Kami</h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Tiga prinsip yang kami pegang teguh dalam mendampingi setiap jamaah menuju Tanah Suci.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { i: HandHeart, t: "Berbuat Baik", d: "Tanpa Henti" },
              { i: Clock,     t: "Bersegera Melayani", d: "Tanpa Nanti" },
              { i: Gift,      t: "Berbagi Kebahagiaan", d: "Tanpa Tapi" },
            ].map((m, i) => (
              <div key={i} data-testid={`moto-${i}`} className="text-center p-8 bg-card border border-border rounded-lg hover:border-accent transition-all">
                <div className="mx-auto w-14 h-14 rounded-full bg-accent/15 grid place-items-center">
                  <m.i className="h-6 w-6 text-accent" strokeWidth={1.75} />
                </div>
                <h3 className="mt-5 font-serif text-2xl text-primary">{m.t}</h3>
                <p className="mt-1 text-accent font-medium tracking-wide text-sm">{m.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fasilitas untuk Jamaah — 8 items from ptbatik.co.id */}
      <section className="py-24 lg:py-32 bg-muted/30" data-testid="facilities-section">
        <div className="container-x">
          <div className="max-w-2xl mb-14">
            <p className="overline mb-5">{t("about.facilitiesOver")}</p>
            <h2 className="font-serif text-4xl lg:text-5xl text-primary tracking-tight">{t("about.facilitiesTitle")}</h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">{t("about.facilitiesSub")}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { i: Ticket,     t: "Jadwal & Tiket Confirmed", d: "Jamaah mendapat kepastian tanggal keberangkatan & maskapai sejak awal." },
              { i: FileCheck,  t: "Visa Umroh & Haji",         d: "Pengurusan visa umroh & haji khusus untuk keperluan ibadah." },
              { i: Building2,  t: "Hotel Terbaik",             d: "Akomodasi hotel & penginapan terbaik serta ternyaman di Makkah dan Madinah." },
              { i: Users,      t: "Pendamping Ustadz",         d: "Umroh didampingi ustadz dan muthawif berpengalaman selama perjalanan." },
              { i: Utensils,   t: "Konsumsi Terjamin",         d: "Konsumsi terjamin dari mulai perjalanan hingga selesai." },
              { i: PackageIcon,t: "Perlengkapan Ibadah",       d: "Perlengkapan ibadah lengkap untuk menunjang kenyamanan jamaah di Tanah Suci." },
              { i: Bus,        t: "Transportasi Nyaman",       d: "Transportasi nyaman untuk memudahkan mobilitas selama perjalanan." },
              { i: Landmark,   t: "City Tour",                 d: "City tour ke tempat-tempat bersejarah di Makkah dan Madinah." },
            ].map((f, i) => (
              <div
                key={i}
                data-testid={`facility-card-${i}`}
                className="p-6 bg-card border border-border rounded-lg hover:border-accent hover:shadow-lg transition-all anim-fade-up"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="h-12 w-12 rounded-full bg-accent/15 grid place-items-center mb-4">
                  <f.i className="h-5 w-5 text-accent" strokeWidth={1.75} />
                </div>
                <h3 className="font-serif text-lg text-primary leading-snug">{f.t}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
